'use client';

/**
 * EditorialLegacyNav — the sticky editorial top nav for the SE3 legacy offering
 * page (Hannah QA, 2026-07-09: "the nav bar is still missing"). The design
 * (StartEngine design update-3/AtomBeam Offering) shows a cream sticky bar with
 * the StartEngine logo, Overview/About/Terms section links, and an Add to
 * Watchlist control — which the classic offering page (control + SE3) never
 * rendered (offering pages ship minimal chrome). This adds it, gated to SE3.
 *
 * The section links smooth-scroll to the classic page's scroll-section anchors
 * (`#home`/`#about`/`#terms`, see components/Offering/Body.jsx). The watchlist
 * reuses the existing self-contained <Follow> control (reads offering data from
 * the query cache). This sits ABOVE the classic section TabMenu — the design
 * has both a top bar and a section-tab row.
 */
import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { ErrorBoundary } from 'react-error-boundary';

import useIsSignedIn from 'hooks/useIsSignedIn';
import { userMenu } from 'utils/links';
import { withSe3AssetPrefix } from 'components/HomepageV3/assetPrefix';
import Follow from '../../HeroSection/Follow';
import { ACCENT_GOLD, TEAL } from './editorialSection';

// Dark-teal logo for the CREAM editorial nav (the /homepage-v3 wordmark is a
// light logo for the dark homepage nav and is invisible on cream). This is the
// exact asset the design references (logo-darkteal.png).
const WORDMARK_SRC = withSe3AssetPrefix('/vinovest/logo-darkteal.png');
const CREAM = '#FCF9F5';

const NAV_LINKS = [
  { label: 'Overview', target: 'home' },
  { label: 'About', target: 'anchor-about' },
  { label: 'Terms', target: 'anchor-terms' },
  { label: 'Updates', target: 'anchor-updates' },
  { label: 'Rewards', target: 'anchor-rewards' },
  { label: 'Discussion', target: 'anchor-discussion' },
  { label: 'Investing FAQs', target: 'anchor-faq' },
];

// Froala campaign pages do not all expose the same sections.  In particular,
// private offerings commonly omit terms, rewards, and discussion.  The
// existing campaign body already renders an anchor only for sections that are
// actually present, making the DOM the source of truth for this navigation.
// Keep Overview available as the page's stable home link.
const getAvailableNavLinks = () => {
  if (typeof document === 'undefined') return NAV_LINKS.slice(0, 1);

  return NAV_LINKS.filter((link) => link.target === 'home' || document.getElementById(link.target));
};

const ACCOUNT_CHIP_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '36px',
  height: '36px',
  padding: 0,
  borderRadius: '999px',
  border: `1px solid ${TEAL}`,
  fontFamily: "var(--bld-font-body, 'IBM Plex Sans', system-ui, sans-serif)",
  color: TEAL,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  transition: 'background-color 0.15s ease',
  '&:hover': { backgroundColor: 'rgba(24,92,101,0.08)' },
};

const EditorialLegacyNav = () => {
  const isSignedIn = useIsSignedIn();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navLinks, setNavLinks] = useState(getAvailableNavLinks);

  useEffect(() => {
    const syncNavLinks = () => {
      setNavLinks((currentLinks) => {
        const nextLinks = getAvailableNavLinks();
        const currentTargets = currentLinks.map(({ target }) => target).join(',');
        const nextTargets = nextLinks.map(({ target }) => target).join(',');
        return currentTargets === nextTargets ? currentLinks : nextLinks;
      });
    };

    syncNavLinks();

    // Froala content can finish mounting after the editorial shell. Observe
    // the page so links appear when their real section anchor arrives, while
    // never advertising sections that the offering does not contain.
    const observer = new MutationObserver(syncNavLinks);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const scrollTo = useCallback((target) => {
    if (typeof document === 'undefined') return;
    if (target === 'home') {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      return;
    }
    document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileMenuOpen(false);
  }, []);

  return (
    <Box
      component="header"
      data-cy="editorial-legacy-nav"
      sx={{
        position: 'sticky',
        top: 0,
        // This is the only campaign navigation on an SE3 Froala offering.
        // Keep it above the legacy page content and its historical TabMenu so
        // it cannot be visually replaced while the page is scrolling.
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '24px',
        padding: { xs: '10px 20px', md: '10px 40px' },
        backgroundColor: 'rgba(252, 249, 245, 0.92)',
        backdropFilter: 'saturate(180%) blur(10px)',
        borderBottom: '1px solid var(--bld-border, #E8E1D6)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '20px', md: '40px' } }}>
        <Box
          component="a"
          href="/"
          aria-label="StartEngine home"
          sx={{ display: 'inline-flex', flexShrink: 0 }}
        >
          <Box
            component="img"
            src={WORDMARK_SRC}
            alt="StartEngine"
            sx={{ height: '28px', width: 'auto', display: 'block' }}
          />
        </Box>
        <Box
          component="nav"
          sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: '22px' }}
        >
          {navLinks.map((link) => (
            <Box
              key={link.target}
              component="button"
              type="button"
              data-cy={`editorial-legacy-nav-${link.target}`}
              onClick={() => scrollTo(link.target)}
              sx={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                fontFamily: "var(--bld-font-body, 'IBM Plex Sans', system-ui, sans-serif)",
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: '#4C463E',
                transition: 'color 0.15s ease',
                '&:hover': { color: ACCENT_GOLD },
              }}
            >
              {link.label}
            </Box>
          ))}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          // Match the Builder editorial navigation: compact outlined action
          // and a 36px profile icon, rather than the legacy wide Follow button.
          '& [data-cy="follow-desktop-button"]': {
            fontFamily: "var(--bld-font-body, 'IBM Plex Sans', system-ui, sans-serif) !important",
            width: 'auto !important',
            minWidth: '0 !important',
            height: '36px',
            padding: '8px 16px !important',
            borderRadius: '999px !important',
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            color: `${TEAL} !important`,
            borderColor: `${TEAL} !important`,
            fontSize: '12px !important',
            fontWeight: '600 !important',
            '& .MuiButton-startIcon': { marginLeft: 0, marginRight: '6px' },
            '& .MuiSvgIcon-root': { fontSize: '20px' },
            '& .MuiTypography-root': {
              fontFamily: "var(--bld-font-body, 'IBM Plex Sans', system-ui, sans-serif) !important",
              fontSize: '12px !important',
              fontWeight: '600 !important',
              letterSpacing: '0.12em',
            },
          },
        }}
      >
        <ErrorBoundary FallbackComponent={() => null}>
          <Follow />
        </ErrorBoundary>
        {/* Match Builder editorial chrome: signed-in users get the compact
            profile affordance; logged-out users retain a text login action. */}
        {isSignedIn === true && (
          <Box
            component="a"
            href={`${userMenu.portfolio}?se3=1`}
            data-cy="editorial-legacy-nav-account"
            aria-label="My Account"
            sx={ACCOUNT_CHIP_SX}
          >
            <PersonOutlineIcon fontSize="small" aria-hidden="true" />
          </Box>
        )}
        {isSignedIn === false && (
          <Box
            component="a"
            href={userMenu.login}
            data-cy="editorial-legacy-nav-login"
            // ACCOUNT_CHIP_SX intentionally makes the signed-in profile
            // affordance a 36px circle. A text login action needs its own
            // intrinsic width; sharing the fixed width clips "Log In" on
            // narrow campaign pages.
            sx={{
              ...ACCOUNT_CHIP_SX,
              width: 'auto',
              minWidth: '68px',
              padding: '0 12px',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            Log In
          </Box>
        )}
        <Box
          component="button"
          type="button"
          data-cy="editorial-legacy-nav-mobile-toggle"
          aria-label="Toggle offering navigation"
          aria-expanded={mobileMenuOpen}
          onClick={() => setMobileMenuOpen((open) => !open)}
          sx={{
            display: { xs: 'inline-flex', lg: 'none' },
            alignItems: 'center',
            justifyContent: 'center',
            width: '36px',
            height: '36px',
            padding: 0,
            color: TEAL,
            background: 'transparent',
            border: `1px solid ${TEAL}`,
            borderRadius: '999px',
            cursor: 'pointer',
          }}
        >
          {mobileMenuOpen ? <CloseIcon fontSize="small" /> : <MenuIcon fontSize="small" />}
        </Box>
      </Box>
      {mobileMenuOpen && (
        <Box
          component="nav"
          data-cy="editorial-legacy-nav-mobile-menu"
          sx={{
            position: 'absolute',
            top: 'calc(100% + 1px)',
            left: 0,
            right: 0,
            display: { xs: 'grid', lg: 'none' },
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: '4px',
            padding: '12px 20px',
            backgroundColor: CREAM,
            borderBottom: '1px solid var(--bld-border, #E8E1D6)',
            boxShadow: '0 10px 24px rgba(11,41,45,0.08)',
          }}
        >
          {navLinks.map((link) => (
            <Box
              key={link.target}
              component="button"
              type="button"
              onClick={() => scrollTo(link.target)}
              data-cy={`editorial-legacy-nav-mobile-${link.target}`}
              sx={{
                minHeight: '40px',
                padding: '8px 0',
                color: '#4C463E',
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: "var(--bld-font-body, 'IBM Plex Sans', system-ui, sans-serif)",
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
              }}
            >
              {link.label}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default EditorialLegacyNav;
